import { LoadingOutlined } from '@ant-design/icons';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Logo from '../../assets/icons/Logo';
import theme from '../../assets/theme';
import useImage from '../../hooks/useImage';
import { LessonAttachment } from '../../services/backendService/backendService';
import { RoundMedia } from '../../services/backendService/types';
import { parseMarkdownToSlateSync } from '../../utils/richTextUtils';
import { RecoverableErrorBoundary } from '../_molecules/RecoverableErrorBoundary';
import {
  charactersPerFullLine,
  isCuripodText,
  parseSlateStringToText,
  parseStringToSlateObject,
  sliceStringToNCharacters,
} from './slateUtil';
import { Slide } from './slide.types';
import SlideResultViewer from './SlideResultViewer';
import {
  createPollElements,
  findRelatedSlidesWithoutIndex,
  mergeVotingTextElementWithQuestionTextElementColor,
} from './slideUtil';
import SlideViewer from './SlideViewer';

const SlideWrapperContentViewer = ({
  slide,
  resultView = false,
  isUsingSockets = true,
  showStaticPoll = false,
  showTextPlaceHolder = false,
  gameId,
  slides,
  tenantId,
  onSlideWasPlayedTimeout,
  aspect,
  staticPollResult,
  children,
  isDiscoverTemplate,
  showPollOptions = true,
  isAuthenticated,
  isOwner,
  uniqueId,
  refetchData,
  lessonAttachments,
  toggleSlideOrResults,
  showWordcloudOverride,
  onShowWordcloudChange,
}: {
  slide: Slide;
  resultView?: boolean;
  toggleSlideOrResults?: () => void;
  isUsingSockets?: boolean;
  showStaticPoll?: boolean;
  staticPollResult?: number[];
  showTextPlaceHolder?: boolean;
  isAuthenticated: boolean;
  gameId?: string;
  tenantId?: string;
  slides?: Slide[];
  uniqueId?: string;
  aspect: {
    width: number;
    height: number;
  };
  refetchData?: boolean;
  onSlideWasPlayedTimeout?: () => void;
  children?: ReactNode;
  isDiscoverTemplate: boolean;
  isOwner: boolean;
  showPollOptions?: boolean;
  lessonAttachments?: LessonAttachment[];
  showWordcloudOverride?: boolean;
  onShowWordcloudChange?: (next: boolean) => void;
}) => {
  const elementsToRender = useMemo(() => {
    if (!slide) return [];
    if (resultView && slide.type !== 'slide') {
      const relatedSlides = findRelatedSlidesWithoutIndex(slide.id, slides || []);
      const firstSlide = relatedSlides[0];
      //TODO this method should actually use the context element to find the correct element.
      if (!firstSlide) return [];
      const elements = firstSlide.elements
        .filter(e => e.lockedFromDeletion === true)
        .map(e => {
          if (e.type === 'text') {
            const textValue = parseSlateStringToText(e.textValue);
            const slateElement = parseStringToSlateObject(e.textValue);
            const firstSlateElement = slateElement.find(e => e.type === 'paragraph');
            // not a paragraph, so we dont show anything
            if (!textValue || !firstSlateElement) {
              return { ...e, textValue: '' };
            }
            const paragraphSize = firstSlateElement.size;
            // Can not find size so we dont show anything
            if (!paragraphSize) {
              return { ...e, textValue: '' };
            }

            const textChild = firstSlateElement.children?.[0];

            const textChildColor = isCuripodText(textChild) ? textChild.color : undefined;

            const maxLength = charactersPerFullLine(paragraphSize);
            const slicedString = sliceStringToNCharacters(textValue, maxLength - 3);

            if (slicedString.trim() === '') {
              return { ...e, textValue: undefined };
            }
            // Use parseMarkdownToSlateSync to parse markdown + math expressions
            const newSlateObject = parseMarkdownToSlateSync(slicedString, {
              size: paragraphSize,
              color: textChildColor || firstSlateElement.color,
              align: firstSlateElement.align,
            });

            return { ...e, textValue: JSON.stringify(newSlateObject) };
          }
          return e;
        });

      if (slide.type !== 'poll' && slide.type !== 'multipleChoice') return elements;

      // multipleChoice result view: the host reveal component renders the
      // option labels itself (sorted by vote count, alongside the bars), so we
      // skip the static pollElements to avoid duplicate labels.
      if (slide.type === 'multipleChoice') return elements;

      // Since poll options gives out text elements we create fictive poll elements that can be rendered out for the slide
      const pollElements = createPollElements({
        pollOptions: slide.pollOptions || [],
        aspect: aspect,
      });
      return [...elements, ...pollElements];
    } else {
      if (slide.type === 'voting') {
        const relatedSlides = findRelatedSlidesWithoutIndex(slide.id, slides || []);
        const questionSlide = relatedSlides[0];
        return mergeVotingTextElementWithQuestionTextElementColor(slide, questionSlide);
      }

      if (slide.type === 'responsegrid' || slide.type === 'podium') {
        const relatedSlides = findRelatedSlidesWithoutIndex(slide.id, slides || []);
        const firstSlide = relatedSlides[0];
        return firstSlide?.elements;
      }
      if (slide.type !== 'poll' && slide.type !== 'multipleChoice') return slide.elements;
      // Poll and multipleChoice follow the same gating: the static bars
      // (in SlideViewer) and the fictive pollOption text elements only
      // render together when both showStaticPoll and showPollOptions are
      // true. Earlier this branch special-cased MC to always inject the
      // text elements, which left floating labels on the slide whenever
      // the consumer (e.g. lessonPreview, thumbnail) had showStaticPoll
      // off — the bars stayed hidden but the labels did not.
      if (showStaticPoll && showPollOptions) {
        const pollElements = createPollElements({
          pollOptions: slide.pollOptions || [],
          aspect: aspect,
        });
        return [...slide.elements, ...pollElements];
      }

      return slide.elements;
    }
  }, [slide, resultView, slides, aspect, showStaticPoll, showPollOptions]);
  return (
    <>
      {children}
      {!resultView && (
        <SlideViewer
          elements={elementsToRender}
          slide={slide}
          gameId={gameId}
          showStaticPoll={showStaticPoll}
          isAuthenticated={isAuthenticated}
          staticPollResult={staticPollResult}
          showTextPlaceHolder={showTextPlaceHolder}
          isDiscoverTemplate={isDiscoverTemplate}
          showPollOptions={showPollOptions}
          isOwner={isOwner}
          lessonAttachments={lessonAttachments}
        />
      )}
      {resultView && (
        <SlideResultViewer
          elements={elementsToRender}
          slide={slide}
          toggleSlideOrResults={toggleSlideOrResults}
          showStaticPoll={showStaticPoll}
          gameId={gameId}
          isAuthenticated={isAuthenticated}
          slides={slides}
          tenantId={tenantId}
          uniqueId={uniqueId}
          onSlideWasPlayedTimeout={onSlideWasPlayedTimeout}
          isDiscoverTemplate={isDiscoverTemplate}
          isUsingSockets={isUsingSockets}
          isOwner={isOwner}
          refetchData={refetchData}
          lessonAttachments={lessonAttachments}
          showWordcloudOverride={showWordcloudOverride}
          onShowWordcloudChange={onShowWordcloudChange}
        />
      )}
    </>
  );
};

//@deprecated in favor of CSSSlideWrapperViewer
const SlideWrapperViewer = ({
  aspect,
  backgroundMedia,
  children,
  hideLogo = false,
  slide,
  resultView = false,
  showStaticPoll = false,
  toggleSlideOrResults,
  preventInteraction = false,
  showTextPlaceHolder = false,
  gameId,
  slides,
  tenantId,
  uniqueId,
  onSlideWasPlayedTimeout,
  staticPollResult,
  isDiscoverTemplate,
  showPollOptions,
  refetchData,
  onClickWithPreventInteraction,
  isAuthenticated = true,

  isUsingSockets,
  isOwner = false,
  preventInteractionContainerStyle = {},
  lessonAttachments,
  variantBackgroundColor,
}: {
  aspect: {
    width: number;
    height: number;
  };
  hideLogo?: boolean;
  preventInteraction?: boolean;
  children?: ReactNode;
  isOwner?: boolean;
  backgroundMedia?: RoundMedia;
  slide: Slide;
  resultView?: boolean;
  showStaticPoll?: boolean;
  showTextPlaceHolder?: boolean;
  gameId?: string;
  uniqueId?: string;
  tenantId?: string;
  slides?: Slide[];
  staticPollResult?: number[];
  onSlideWasPlayedTimeout?: () => void;
  isDiscoverTemplate: boolean;
  showPollOptions?: boolean;
  isUsingSockets?: boolean;
  isAuthenticated?: boolean;
  trackDownload?: boolean;
  refetchData?: boolean;
  toggleSlideOrResults?: () => void;
  onClickWithPreventInteraction?: () => void;
  preventInteractionContainerStyle?: React.CSSProperties;
  lessonAttachments?: LessonAttachment[];
  variantBackgroundColor?: string; // only used for templates / generators
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerFieldRef = useRef<HTMLDivElement>(null);
  const maxContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number>();
  const { url: backgroundMediaUrl, isLoading: isLoadingBackground } = useImage({
    fileId: backgroundMedia?.fileId || null,
    type: backgroundMedia?.fileMimeType,
    cropConfig: backgroundMedia?.cropConfig,
    isAuthenticated,
    isDiscoverTemplate,
    isOwner,
  });
  useEffect(() => {
    /* I noticed resize observer doesn't trigger when width is fixed, styles don't change when the window changes
      therefore we have a MaxSizeContainer that serves as a max reference.
      Then we have a container that we observe for changes in width. Its height and width  = 100% making sure resizeObserver always trigers.
      Then we have the actual container that we set the width on.
      We use the refs to get the widths and heights of the divs at the same time as the resize observer triggers.
    */

    const calculateAndSetMaxWidth = () => {
      const aspectRatio = aspect.width / aspect.height;
      const maxHeight = maxContainerRef.current?.clientHeight || 0;
      const maxWidth = maxContainerRef.current?.clientWidth || 0;

      // If we set max width will we get over height?
      if (maxWidth / aspectRatio > maxHeight) {
        setMaxWidth(maxHeight * aspectRatio);
      } else {
        setMaxWidth(undefined);
      }
    };

    const calculateAndSetScale = () => {
      const scaleContainerWidth = containerRef.current?.clientWidth || 0;
      const scale = scaleContainerWidth / aspect.width;
      setScale(scale);
    };

    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        calculateAndSetMaxWidth();
        calculateAndSetScale();
      });
    });
    ro.observe(containerFieldRef.current as HTMLElement);

    // Upon first render
    calculateAndSetMaxWidth();
    calculateAndSetScale();

    return () => {
      ro.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MaxSizeContainer ref={maxContainerRef}>
      <FieldContainer ref={containerFieldRef} $maxWidth={maxWidth}>
        {/* Reference copntainer taht we observe for scale changes */}

        <RefContainer
          ref={containerRef}
          $maxWidth={maxWidth}
          $aspectRatio={aspect.width / aspect.height}
        >
          {/* Background container that sets background image */}
          <BackgroundContainer
            $backgroundImageSrc={backgroundMediaUrl}
            $imageWidth={aspect.width * scale}
            $brightness={backgroundMedia?.brightness}
            $isColorBackground={backgroundMedia?.fileMimeType === 'image/color'}
          >
            {/* Fixed height that sets the actual size of the slide */}
            <FixedHeightContainer
              $height={aspect.height * scale}
              $width={aspect.width * scale}
            >
              {/* The Scaler that transforms the FixedHeightContainer to fit with the avaible viewbox */}
              <ScaleContainer
                $height={aspect.height}
                $width={aspect.width}
                $scale={scale}
                $backgroundColor={variantBackgroundColor ?? 'transparent'}
              >
                <RecoverableErrorBoundary>
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <SlideWrapperContentViewer
                      aspect={aspect}
                      slide={slide}
                      isAuthenticated={isAuthenticated}
                      gameId={gameId}
                      refetchData={refetchData}
                      onSlideWasPlayedTimeout={onSlideWasPlayedTimeout}
                      resultView={resultView}
                      staticPollResult={staticPollResult}
                      showStaticPoll={showStaticPoll}
                      showTextPlaceHolder={showTextPlaceHolder}
                      slides={slides}
                      tenantId={tenantId}
                      isDiscoverTemplate={isDiscoverTemplate}
                      showPollOptions={showPollOptions}
                      isUsingSockets={isUsingSockets}
                      isOwner={isOwner}
                      uniqueId={uniqueId}
                      toggleSlideOrResults={toggleSlideOrResults}
                      lessonAttachments={lessonAttachments}
                    >
                      {!!children && <>{children}</>}
                      {!!children && <OverFlowContainer>{children}</OverFlowContainer>}
                    </SlideWrapperContentViewer>
                    {preventInteraction && (
                      <PreventInteractionContainer
                        style={
                          !!onClickWithPreventInteraction
                            ? { cursor: 'pointer', ...preventInteractionContainerStyle }
                            : { ...preventInteractionContainerStyle }
                        }
                        onClick={() => {
                          if (onClickWithPreventInteraction) {
                            onClickWithPreventInteraction();
                          }
                        }}
                      />
                    )}
                  </div>
                </RecoverableErrorBoundary>
              </ScaleContainer>
            </FixedHeightContainer>
            {/* Top right logo */}
            {!hideLogo && (
              <LogoContainer>
                <Logo />
              </LogoContainer>
            )}
            {isLoadingBackground && (
              <BackgroundLoaderContainer>
                <LoadingOutlined
                  style={{ fontSize: 20, color: theme.colors.orange }}
                  spin
                />
              </BackgroundLoaderContainer>
            )}
          </BackgroundContainer>
        </RefContainer>
      </FieldContainer>
    </MaxSizeContainer>
  );
};

const MaxSizeContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 0;
  width: 12%;
  height: 12%;
  right: 2%;
  color: ${theme.colors.orange};
  svg {
    width: 100%;
    height: 100%;
    position: absolute;
  }
`;

const FieldContainer = styled.div<{
  $maxWidth?: number;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;

  // Change the flex center css to scale horisontally or vertically
  ${({ $maxWidth }) => css`
    flex-direction: ${$maxWidth ? 'column' : 'row'};
  `}
`;

const RefContainer = styled.div<{ $aspectRatio: number; $maxWidth?: number }>`
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  position: relative;
  background-color: ${theme.colors.background};

  ${({ $maxWidth }) =>
    $maxWidth
      ? css`
          width: ${$maxWidth}px;
        `
      : css`
          width: 100%;
        `}
`;

const OverFlowContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;
const FixedHeightContainer = styled.div<{ $width: number; $height: number }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  max-width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const BackgroundLoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreventInteractionContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  border-radius: 10px;
  left: 0;
`;
const BackgroundContainer = styled.div<{
  $brightness?: number | null;
  $isColorBackground: boolean;
  $imageWidth: number;
  $backgroundImageSrc?: string | null;
}>`
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.black};
  overflow: hidden;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  ${({ $backgroundImageSrc, $brightness, $isColorBackground }) =>
    $backgroundImageSrc && !$isColorBackground
      ? // Background media is selected
        css`
          background: url(${$backgroundImageSrc});
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
        `
      : $isColorBackground && $backgroundImageSrc
      ? // Color background is selected
        css`
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: ${$backgroundImageSrc};
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                ${$backgroundImageSrc};
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                ${$backgroundImageSrc || theme.colors.black};
            `};
        `
      : // No background is selected
        css`
          background: ${theme.colors.background};
        `};
  background-repeat: no-repeat;
  height: 100%;
`;
const ScaleContainer = styled.div<{
  $scale: number;
  $width: number;
  $height: number;
  $backgroundColor?: string;
}>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: scale(${({ $scale }) => $scale});
  background-color: ${({ $backgroundColor = 'transparent' }) => $backgroundColor};
  position: absolute;
  transform-origin: top left;
  top: 0;
  left: 0;
`;

export default SlideWrapperViewer;

/* 
This uses css instead of resize observer to handle height / width scaling.
 */
export const CSSSlideWrapperViewer = ({
  aspect,
  backgroundMedia,
  children,
  hideLogo = false,
  slide,
  resultView = false,
  showStaticPoll = false,
  preventInteraction = false,
  showTextPlaceHolder = false,
  gameId,
  slides,
  tenantId,
  uniqueId,
  onSlideWasPlayedTimeout,
  staticPollResult,
  isDiscoverTemplate,
  showPollOptions,
  refetchData,
  onClickWithPreventInteraction,
  isAuthenticated = true,
  lessonAttachments,
  isUsingSockets,
  isOwner = false,
  preventInteractionContainerStyle = {},
  toggleSlideOrResults,
  showWordcloudOverride,
  onShowWordcloudChange,
}: {
  aspect: {
    width: number;
    height: number;
  };
  hideLogo?: boolean;
  preventInteraction?: boolean;
  children?: ReactNode;
  isOwner?: boolean;
  backgroundMedia?: RoundMedia;
  slide: Slide;
  resultView?: boolean;
  showStaticPoll?: boolean;
  showTextPlaceHolder?: boolean;
  gameId?: string;
  uniqueId?: string;
  tenantId?: string;
  slides?: Slide[];
  staticPollResult?: number[];
  onSlideWasPlayedTimeout?: () => void;
  isDiscoverTemplate: boolean;
  showPollOptions?: boolean;
  isUsingSockets?: boolean;
  isAuthenticated?: boolean;
  refetchData?: boolean;
  onClickWithPreventInteraction?: () => void;
  preventInteractionContainerStyle?: React.CSSProperties;
  lessonAttachments?: LessonAttachment[];
  toggleSlideOrResults?: () => void;
  showWordcloudOverride?: boolean;
  onShowWordcloudChange?: (next: boolean) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerFieldRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const { url: backgroundMediaUrl, isLoading: isLoadingBackground } = useImage({
    fileId: backgroundMedia?.fileId || null,
    type: backgroundMedia?.fileMimeType,
    cropConfig: backgroundMedia?.cropConfig,
    isAuthenticated,
    isDiscoverTemplate,
    isOwner,
  });
  useEffect(() => {
    const calculateAndSetScale = () => {
      const scaleContainerWidth = containerRef.current?.clientWidth ?? 0;
      if (scaleContainerWidth <= 0) return;
      setScale(scaleContainerWidth / aspect.width);
    };

    let rafId: number | null = null;
    const scheduleRecompute = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        calculateAndSetScale();
      });
    };

    // Safari fullscreen (green button / requestFullscreen) does not always
    // fire a window resize event nor trigger ResizeObserver until the layout
    // has fully settled, which can take more than one frame. Recompute on
    // the next frame and again after a short delay so we always catch the
    // post-transition size.
    const pendingTimeouts = new Set<number>();
    const scheduleSettledRecompute = () => {
      scheduleRecompute();
      const timeoutId = window.setTimeout(() => {
        pendingTimeouts.delete(timeoutId);
        scheduleRecompute();
      }, 250);
      pendingTimeouts.add(timeoutId);
    };

    const ro = new ResizeObserver(scheduleRecompute);
    if (containerFieldRef.current) ro.observe(containerFieldRef.current);
    // Also observe the ref container we actually measure: on aspect-ratio
    // media-query boundaries (very wide / very tall viewports) its width can
    // change without the field container's box changing.
    if (containerRef.current) ro.observe(containerRef.current);

    // Fallback: catches cases where the RO is briefly disconnected (parent
    // remounts, overlay toggles) or skipped by the browser's loop guard.
    window.addEventListener('resize', scheduleRecompute);
    document.addEventListener('fullscreenchange', scheduleSettledRecompute);
    document.addEventListener('webkitfullscreenchange', scheduleSettledRecompute);
    window.addEventListener('orientationchange', scheduleSettledRecompute);
    // visualViewport fires when window 'resize' does not (Safari fullscreen,
    // pinch-zoom, on-screen keyboard, etc.).
    window.visualViewport?.addEventListener('resize', scheduleRecompute);

    calculateAndSetScale();

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      pendingTimeouts.forEach(id => window.clearTimeout(id));
      pendingTimeouts.clear();
      ro.disconnect();
      window.removeEventListener('resize', scheduleRecompute);
      document.removeEventListener('fullscreenchange', scheduleSettledRecompute);
      document.removeEventListener('webkitfullscreenchange', scheduleSettledRecompute);
      window.removeEventListener('orientationchange', scheduleSettledRecompute);
      window.visualViewport?.removeEventListener('resize', scheduleRecompute);
    };
  }, [aspect.width, aspect.height]);

  return (
    <MaxSizeContainer>
      <NewFieldContainer ref={containerFieldRef}>
        {/* Reference container that we observe for scale changes */}

        <NewRefContainer ref={containerRef} $aspectRatio={aspect.width / aspect.height}>
          {/* Background container that sets background image */}
          <BackgroundContainer
            $backgroundImageSrc={backgroundMediaUrl}
            $imageWidth={aspect.width * scale}
            $brightness={backgroundMedia?.brightness}
            $isColorBackground={backgroundMedia?.fileMimeType === 'image/color'}
          >
            {/* Fixed height that sets the actual size of the slide */}
            <FixedHeightContainer
              $height={aspect.height * scale}
              $width={aspect.width * scale}
            >
              {/* The Scaler that transforms the FixedHeightContainer to fit with the avaible viewbox */}
              <ScaleContainer
                $height={aspect.height}
                $width={aspect.width}
                $scale={scale}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <SlideWrapperContentViewer
                    aspect={aspect}
                    slide={slide}
                    isAuthenticated={isAuthenticated}
                    gameId={gameId}
                    refetchData={refetchData}
                    onSlideWasPlayedTimeout={onSlideWasPlayedTimeout}
                    resultView={resultView}
                    staticPollResult={staticPollResult}
                    showStaticPoll={showStaticPoll}
                    showTextPlaceHolder={showTextPlaceHolder}
                    slides={slides}
                    tenantId={tenantId}
                    isDiscoverTemplate={isDiscoverTemplate}
                    showPollOptions={showPollOptions}
                    isUsingSockets={isUsingSockets}
                    isOwner={isOwner}
                    uniqueId={uniqueId}
                    toggleSlideOrResults={toggleSlideOrResults}
                    lessonAttachments={lessonAttachments}
                    showWordcloudOverride={showWordcloudOverride}
                    onShowWordcloudChange={onShowWordcloudChange}
                  >
                    {!!children && <OverFlowContainer>{children}</OverFlowContainer>}
                  </SlideWrapperContentViewer>
                  {preventInteraction && (
                    <PreventInteractionContainer
                      style={
                        !!onClickWithPreventInteraction
                          ? { cursor: 'pointer', ...preventInteractionContainerStyle }
                          : { ...preventInteractionContainerStyle }
                      }
                      onClick={() => {
                        if (onClickWithPreventInteraction) {
                          onClickWithPreventInteraction();
                        }
                      }}
                    />
                  )}
                </div>
              </ScaleContainer>
            </FixedHeightContainer>
            {/* Top right logo */}
            {!hideLogo && (
              <LogoContainer>
                <Logo />
              </LogoContainer>
            )}
            {isLoadingBackground && (
              <BackgroundLoaderContainer>
                <LoadingOutlined
                  style={{ fontSize: 20, color: theme.colors.orange }}
                  spin
                />
              </BackgroundLoaderContainer>
            )}
          </BackgroundContainer>
        </NewRefContainer>
      </NewFieldContainer>
    </MaxSizeContainer>
  );
};

const NewFieldContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NewRefContainer = styled.div<{ $aspectRatio: number; $maxWidth?: number }>`
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  position: relative;
  background-color: ${theme.colors.background};

  @media (max-aspect-ratio: ${({ $aspectRatio }) => $aspectRatio}) {
    // checks if we are on a wider screen and uses max width as fixed width where height increases based on aspect-ratio
    width: 100%;
  }
  @media (min-aspect-ratio: ${({ $aspectRatio }) => $aspectRatio}) {
    // checks if we are on a taller screen and uses max height as fixed height where width increases based on aspect-ratio
    height: 100%;
  }
`;
